import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../axios';
import { Card, PageHeader, Button, InputWithError } from '../UI';
import classes from './ClaimDevice.module.css';

const ClaimDevice = () => {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [loading, setLoading] = useState(false);
  const [inputError, setInputError] = useState('');

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await axios.get('/projects');
      if (response.data && response.data.success && response.data.projects) {
        setProjects(response.data.projects);
      } else {
        setProjects([]);
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
      setProjects([]);
    }
  };

  const handleAddCode = async () => {
    const trimmed = code.trim();
    if (!trimmed) {
      setInputError('');
      return;
    }

    // Check if code already exists
    if (codes.some(item => item.code === trimmed)) {
      setInputError('This code is already in the list');
      return;
    }

    setInputError('');

    // Check device via API first, before adding to list
    try {
      const response = await axios.post('/devices/check-device', {
        deviceUuid: trimmed
      });

      if (response.data.success) {
        // Device can be registered - add to list
        const newCodeItem = { code: trimmed, status: 'pending', error: null };
        setCodes([...codes, newCodeItem]);
        setCode('');
      } else {
        setInputError(response.data.message || 'Device cannot be registered');
      }
    } catch (error) {
      let errorMessage = 'Failed to check device';
      
      if (error.response) {
        const status = error.response.status;
        const message = error.response.data?.message || '';
        
        if (status === 404) {
          errorMessage = 'Device not found';
        } else if (status === 400) {
          errorMessage = message || 'Invalid code format';
        } else {
          errorMessage = message || errorMessage;
        }
      }

      // Show error message instead of adding to list
      setInputError(errorMessage);
    }
  };

  const handleRemoveCode = (index) => {
    setCodes(codes.filter((_, i) => i !== index));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAddCode();
    }
  };

  const handleClaim = async () => {
    // Filter only pending codes (validated and ready to claim)
    const validCodes = codes.filter(item => item.status === 'pending');
    if (validCodes.length === 0) {
      alert('Please add at least one valid device code');
      return;
    }

    setLoading(true);

    try {
      // Prepare array of deviceUuids
      const deviceUuids = validCodes.map(item => item.code);

      // Claim all devices at once
      const requestBody = {
        deviceUuid: deviceUuids
      };
      
      // Add projectId if selected
      if (selectedProject && selectedProject.trim() !== '') {
        requestBody.projectId = selectedProject;
      }

      const response = await axios.post('/devices/register-by-code', requestBody);

      if (response.data && response.data.results) {
        // Update codes based on results
        const results = response.data.results;
        
        results.forEach(result => {
          setCodes(prev => prev.map(item => {
            if (item.code === result.deviceUuid) {
              if (result.success) {
                return { ...item, status: 'success', error: null };
              } else {
                return { 
                  ...item, 
                  status: 'error', 
                  error: result.message || 'Failed to claim device',
                  isAlreadyClaimed: result.message?.includes('already registered')
                };
              }
            }
            return item;
          }));
        });

        // Check results
        const allSuccess = results.every(r => r.success);
        const registeredCount = response.data.registered || 0;

        if (allSuccess) {
          alert(`Сlaimed successfully!`);
          navigate('/devices');
        } else {
          // Some devices failed to claim (e.g., race condition - device was registered by another user)
          if (registeredCount > 0) {
            alert(`${registeredCount} device(s) claimed successfully. Please review errors below.`);
          } else {
            alert('Failed to claim devices. Please review errors below.');
          }
        }
      }
    } catch (error) {
      console.error('Failed to claim devices:', error);
      let errorMessage = 'Failed to claim devices';
      
      if (error.response) {
        errorMessage = error.response.data?.message || errorMessage;
      }
      
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classes.claimDevice}>
      <PageHeader title="Claim device" lastPage={true} />
      
      <Card padding="large">
        <div className={classes.instructions}>
          <strong>&lt; visual + instructions &gt;</strong>
        </div>

        <InputWithError
          label="Enter unique code"
          value={code}
          onChange={(e) => {
            setCode(e.target.value);
            setInputError('');
          }}
          onKeyPress={handleKeyPress}
          error={inputError}
          buttonText="Add"
          onButtonClick={handleAddCode}
          buttonDisabled={!code.trim()}
        />

        {codes.length > 0 && (
          <div className={classes.codesList}>
            {codes.map((codeItem, index) => (
              <div 
                key={index} 
                className={`${classes.codeItem} ${
                  codeItem.status === 'error' ? classes.codeItemError : ''
                } ${
                  codeItem.status === 'success' ? classes.codeItemSuccess : ''
                } ${
                  codeItem.isAlreadyClaimed ? classes.codeItemWarning : ''
                }`}
              >
                <span className={classes.codeText}>{codeItem.code}</span>
                {codeItem.status === 'error' && (
                  <span className={classes.errorMessage}>
                    {codeItem.error}
                  </span>
                )}
                {codeItem.status === 'success' && (
                  <span className={classes.successMessage}>Claimed</span>
                )}
                {codeItem.status === 'pending' && loading && (
                  <span className={classes.pendingMessage}>Processing...</span>
                )}
                {!loading && (
                  <button 
                    onClick={() => handleRemoveCode(index)}
                    className={classes.removeButton}
                    title="Remove code"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        <div className={classes.bottomSection}>
          <div className={classes.projectSection}>
            <span className={classes.projectLabel}>Assign to project</span>
            <select
              className={classes.projectSelect}
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
            >
              <option value="">Select...</option>
              {projects.map((project) => (
                <option key={project._id} value={project._id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>
          <Button
            onClick={handleClaim}
            variant="primary"
            disabled={loading || codes.length === 0}
          >
            {loading ? 'Claiming...' : 'Claim'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default ClaimDevice;